"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InputNumber = void 0;
var tslib_1 = require("tslib");
var react_1 = tslib_1.__importStar(require("react"));
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var controlled_1 = require("../form/controlled");
var get_precision_1 = require("../_util/get-precision");
var config_context_1 = require("../_util/config-context");
var noop_1 = require("../_util/noop");
var key_map_1 = require("../_util/key-map");
var merge_event_props_1 = require("../_util/merge-event-props");
var forward_ref_with_statics_1 = require("../_util/forward-ref-with-statics");
var util_1 = require("../util");
var DISABLED_CLS = "is-disabled";
/**
 * 数字输入组件
 */
exports.InputNumber = forward_ref_with_statics_1.forwardRefWithStatics(function InputNumber(props, ref) {
    var _a, _b, _c;
    var classPrefix = config_context_1.useConfig().classPrefix;
    var size = props.size, unit = props.unit, disabled = props.disabled, style = props.style, className = props.className, _d = props.onFocus, onFocus = _d === void 0 ? noop_1.noop : _d, _e = props.onBlur, onBlur = _e === void 0 ? noop_1.noop : _e, hideButton = props.hideButton, _f = props.inputProps, inputProps = _f === void 0 ? {} : _f;
    var _g = useInputNumberHooks(props), minus = _g.minus, input = _g.input, plus = _g.plus;
    return (react_1.default.createElement("span", tslib_1.__assign({ ref: ref, className: classnames_1.default(classPrefix + "-inputnum", className, (_a = {
                "size-l": size === "l"
            },
            _a[DISABLED_CLS] = disabled,
            _a)), style: style }, merge_event_props_1.mergeEventProps(props)),
        !hideButton && (react_1.default.createElement("span", { className: classnames_1.default(classPrefix + "-inputnum__minus", (_b = {},
                _b[DISABLED_CLS] = minus.disabled,
                _b)), onMouseDown: disabled ? noop_1.noop : minus.handleMouseDown }, "-")),
        react_1.default.createElement("input", tslib_1.__assign({ className: classnames_1.default(classPrefix + "-input", inputProps.className), value: input.value || "", disabled: disabled }, inputProps, { onChange: util_1.callBoth(input.handleChange, inputProps.onChange), onFocus: util_1.callBoth(onFocus, inputProps.onFocus), onBlur: util_1.callBoth(function (e) {
                onBlur(e);
                input.handleBlur(e);
            }, inputProps.onBlur), onKeyDown: util_1.callBoth(input.handleKeyDown, inputProps.onKeyDown) })),
        !hideButton && (react_1.default.createElement("span", { className: classnames_1.default(classPrefix + "-inputnum__plus", (_c = {},
                _c[DISABLED_CLS] = plus.disabled,
                _c)), onMouseDown: disabled ? noop_1.noop : plus.handleMouseDown }, "+")),
        unit && (react_1.default.createElement("div", { className: classPrefix + "-form__help-text--inline" }, unit))));
}, {
    defaultLabelAlign: "middle",
});
exports.InputNumber.displayName = "InputNumber";
function getDefaultDefaultValue(min, max) {
    if (min && min > 0) {
        return min;
    }
    if (max && max < 0) {
        return max;
    }
    return 0;
}
var defaultFormatter = function (x) { return String(x); };
var defaultParser = function (x) { return Number(x); };
/**
 * InputNumber 状态管理
 */
function useInputNumberHooks(props) {
    var _a;
    var step = props.step;
    var _b = controlled_1.useDefaultValue(props, getDefaultDefaultValue(props.min, props.max)), min = _b.min, max = _b.max, value = _b.value, _c = _b.onChange, onChange = _c === void 0 ? noop_1.noop : _c, _d = _b.onInputChange, onInputChange = _d === void 0 ? noop_1.noop : _d, allowEmpty = _b.allowEmpty, _e = _b.formatter, formatter = _e === void 0 ? defaultFormatter : _e, _f = _b.parser, parser = _f === void 0 ? defaultParser : _f;
    var isValidNumber = function (num) {
        return typeof num === "number" && !Number.isNaN(num);
    };
    step = isValidNumber(step) ? step : 1;
    var precision = (_a = props.precision) !== null && _a !== void 0 ? _a : get_precision_1.getPrecision(step);
    var hasMax = isValidNumber(max);
    var hasMinus = isValidNumber(min);
    var _g = tslib_1.__read(react_1.useState(allowEmpty && value === null), 2), isEmpty = _g[0], setIsEmpty = _g[1];
    var _h = tslib_1.__read(react_1.useState(null), 2), inputValue = _h[0], setInputValue = _h[1];
    var hasInputValue = inputValue !== null;
    var parse = function (newValue) {
        return newValue.trim() === "" ? 0 : parseFloat(newValue);
    };
    var getCurrentValue = function () {
        var currentValue = value;
        if (hasInputValue && /\d/.test(inputValue)) {
            currentValue = parse(inputValue);
            // 解析失败，退回当前值
            if (Number.isNaN(currentValue)) {
                currentValue = value;
            }
        }
        return currentValue;
    };
    var canMinus = function (value) { return !hasMinus || value > min; };
    var canPlus = function (value) { return !hasMax || value < max; };
    var commit = function (_newValue, event) {
        var newValue = _newValue;
        // 从 input 回调过来的，是字符串
        if (typeof newValue === "string") {
            if (allowEmpty && !newValue) {
                onChange(null, { event: event });
                return;
            }
            newValue = parser(newValue);
        }
        // 不能小于最小值
        if (hasMinus) {
            newValue = Math.max(min, newValue);
        }
        // 不能大于最大值
        if (hasMax) {
            newValue = Math.min(max, newValue);
        }
        // 解析失败，还原旧值
        if (Number.isNaN(newValue)) {
            newValue = value;
        }
        // 还原预置初始值
        if (typeof newValue !== "number" || Number.isNaN(newValue)) {
            newValue = getDefaultDefaultValue(min, max);
        }
        // 处理精度
        newValue = parse(newValue.toFixed(precision));
        onChange(newValue, { event: event });
        onInputChange(formatter(newValue));
    };
    // value 发生变更，清空暂存的值
    react_1.useEffect(function () { return setInputValue(null); }, [value]);
    // 长按增减按钮会持续操作，鼠标抬起，或者组件销毁时要清空
    var autoStepTimer = react_1.useRef(0);
    var clear = function () { return clearTimeout(autoStepTimer.current); };
    react_1.useEffect(function () {
        window.addEventListener("mouseup", clear, true);
        // destroy
        return function () {
            clear();
            window.removeEventListener("mouseup", clear);
        };
    }, []);
    // 为按钮提供步长调整逻辑
    var stepper = function (step) {
        // 步长的符号影响是否允许进行下一步长的判断方法
        var canStep = step > 0 ? canPlus : canMinus;
        return {
            disabled: !canStep(value),
            // 点击按钮的时候，先变更一次。然后，只要鼠标不抬起，自动连续变更
            handleMouseDown: function (evt) {
                // 只有鼠标左键可用
                if (evt.nativeEvent.which >= 2) {
                    return;
                }
                if (allowEmpty) {
                    setIsEmpty(false);
                }
                var currentValue = getCurrentValue();
                var performStep = function () {
                    if (canStep(currentValue)) {
                        commit((currentValue += step), evt);
                    }
                };
                performStep();
                clear();
                // 1 秒后，开始自动递增
                autoStepTimer.current = setTimeout(function () {
                    var autoPerform = function () {
                        performStep();
                        autoStepTimer.current = setTimeout(autoPerform, 50);
                    };
                    autoPerform();
                }, 700);
                // 保留事件内容，否则在自动步长执行的时候，事件不可用
                evt.persist();
                // 阻止事件变成 blur 事件，导致二次设置
                evt.preventDefault();
            },
        };
    };
    return {
        minus: stepper(-step),
        plus: stepper(step),
        input: {
            value: hasInputValue || isEmpty
                ? inputValue
                : formatter(Number(value).toFixed(precision)),
            handleChange: function (evt) {
                var value = evt.target.value;
                if (allowEmpty) {
                    setIsEmpty(!value);
                }
                setInputValue(value);
                onInputChange(value);
            },
            handleBlur: function (evt) {
                commit(evt.target.value, evt);
                setInputValue(null);
            },
            handleKeyDown: function (evt) {
                var currentValue = getCurrentValue();
                if (canPlus(currentValue) && evt.key === key_map_1.KeyMap.Up) {
                    // ArrowUp
                    evt.preventDefault();
                    if (allowEmpty) {
                        setIsEmpty(false);
                    }
                    commit(currentValue + step, evt);
                }
                else if (canMinus(currentValue) && evt.key === key_map_1.KeyMap.Down) {
                    // ArrowDown
                    evt.preventDefault();
                    if (allowEmpty) {
                        setIsEmpty(false);
                    }
                    commit(currentValue - step, evt);
                }
                // Enter
                if (evt.key === key_map_1.KeyMap.Enter) {
                    evt.preventDefault();
                    commit(evt.currentTarget.value, evt);
                    setInputValue(null);
                }
            },
        },
    };
}
//# sourceMappingURL=InputNumber.js.map